/**
 * Bundle offers — Daruma
 *
 * Two responsibilities:
 *  1. Keep each offer card in sync with the selected variant (image + price),
 *     by listening to the theme's "variant:change" event on the product form.
 *  2. When an offer with a companion product is selected, intercept the product
 *     form submission (before the theme's own <product-form> handler) and add
 *     both items to the cart in a single request, replicating the theme's
 *     post-add flow (events, cart drawer refresh, error handling).
 */

function formatMoney(cents, format) {
  format = format || window.themeVariables?.settings?.moneyFormat || '{{amount}}';

  const amount = cents / 100;

  const formatWith = (decimals, thousands, decimalSeparator) => {
    const fixed = amount.toFixed(decimals);
    const parts = fixed.split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return decimals > 0 ? integerPart + decimalSeparator + parts[1] : integerPart;
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
    switch (placeholder) {
      case 'amount':
        return formatWith(2, ',', '.');
      case 'amount_no_decimals':
        return formatWith(0, ',', '.');
      case 'amount_with_comma_separator':
        return formatWith(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return formatWith(0, '.', ',');
      case 'amount_with_space_separator':
        return formatWith(2, ' ', ',');
      case 'amount_no_decimals_with_space_separator':
        return formatWith(0, ' ', ',');
      case 'amount_with_apostrophe_separator':
        return formatWith(2, "'", '.');
      case 'amount_with_period_and_space_separator':
        return formatWith(2, ' ', '.');
      default:
        return formatWith(2, ',', '.');
    }
  });
}

function sizedImageUrl(src, width) {
  try {
    const url = new URL(src, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch (error) {
    return src;
  }
}

/**
 * Pose product-info--bundle-selected sur la fiche produit quand une offre
 * avec article associé est cochée. theme.css s'appuie sur cette classe pour
 * masquer le bouton de paiement accéléré (un seul article) au profit du
 * bouton « Acheter maintenant » bundle — une bascule JS plutôt que :has(),
 * ignoré par les navigateurs d'avant 2023.
 */
function syncBundleExpressSwap(target) {
  const productInfo = target.closest('.product-info');

  if (!productInfo) {
    return;
  }

  const companionChecked = productInfo.querySelector('input[data-bundle-offer][data-companion-id]:checked');
  productInfo.classList.toggle('product-info--bundle-selected', Boolean(companionChecked));
}

class BundleOffer extends HTMLElement {
  connectedCallback() {
    // État initial (offre avec article associé précochée, ou re-rendu de la section)
    syncBundleExpressSwap(this);

    this.form = document.forms[this.getAttribute('form-id')];

    if (!this.form) {
      return;
    }

    this._onVariantChange = (event) => this.updateFromVariant(event.detail.variant);
    this.form.addEventListener('variant:change', this._onVariantChange);

    this._onCompanionVariantClick = (event) => this.selectCompanionVariant(event);
    this.addEventListener('click', this._onCompanionVariantClick);
  }

  disconnectedCallback() {
    if (this.form && this._onVariantChange) {
      this.form.removeEventListener('variant:change', this._onVariantChange);
    }

    if (this._onCompanionVariantClick) {
      this.removeEventListener('click', this._onCompanionVariantClick);
    }
  }

  /**
   * Choix d'une variante de l'article associé (pastilles rendues par
   * snippets/bundle-offer.liquid) : met à jour l'ID/prix envoyés au panier,
   * l'image de l'article associé, le nom affiché, puis coche l'offre.
   */
  selectCompanionVariant(event) {
    const button = event.target.closest('[data-bundle-variant]');

    if (!button || button.disabled || !this.contains(button)) {
      return;
    }

    event.preventDefault();

    const radio = this.querySelector('input[data-bundle-offer]');

    if (!radio) {
      return;
    }

    radio.dataset.companionId = button.dataset.variantId;
    radio.dataset.companionPrice = button.dataset.variantPrice;

    const companionImage = this.querySelector('[data-bundle-companion-image]');

    if (companionImage && button.dataset.variantImage) {
      companionImage.removeAttribute('srcset');
      companionImage.src = button.dataset.variantImage;
    }

    const nameElement = this.querySelector('[data-bundle-variant-name]');

    if (nameElement) {
      nameElement.textContent = button.dataset.variantTitle || '';
    }

    this.querySelectorAll('[data-bundle-variant]').forEach((candidate) => {
      candidate.setAttribute('aria-pressed', candidate === button ? 'true' : 'false');
    });

    if (!radio.checked && !radio.disabled) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.refreshPrices(radio);
  }

  updateFromVariant(variant) {
    if (!variant || !variant.id) {
      return;
    }

    const image = this.querySelector('[data-bundle-main-image]');
    const previewSrc = variant.featured_media?.preview_image?.src;

    if (image && previewSrc) {
      image.removeAttribute('srcset');
      image.src = sizedImageUrl(previewSrc, 120);
    }

    const radio = this.querySelector('input[data-bundle-offer]');

    if (radio) {
      radio.dataset.mainPrice = String(variant.price);

      if (radio.dataset.discountMap) {
        try {
          const map = JSON.parse(radio.dataset.discountMap);
          if (map[variant.id] != null) {
            radio.dataset.discountPercent = String(map[variant.id]);
          }
        } catch (_) {
          /* carte de remises illisible : on garde la remise courante */
        }
      }

      this.refreshPrices(radio);
    }
  }

  refreshPrices(radio) {
    const mainPrice = parseInt(radio.dataset.mainPrice, 10) || 0;
    const companionPrice = parseInt(radio.dataset.companionPrice, 10) || 0;
    const discountPercent = parseFloat(radio.dataset.discountPercent) || 0;

    const totalPrice = mainPrice + companionPrice;
    const finalPrice = Math.round((totalPrice * (100 - discountPercent)) / 100);

    const compareElement = this.querySelector('[data-bundle-compare-price]');
    const finalElement = this.querySelector('[data-bundle-final-price]');

    if (compareElement) {
      this.replacePriceText(compareElement, formatMoney(totalPrice));
      compareElement.hidden = discountPercent <= 0;
    }

    if (finalElement) {
      this.replacePriceText(finalElement, formatMoney(finalPrice));
    }

    const badgeElement = this.querySelector('[data-bundle-discount-badge]');

    if (badgeElement) {
      badgeElement.textContent = `-${Math.round(discountPercent)}%`;
      badgeElement.hidden = discountPercent <= 0;
    }
  }

  /* Keep the leading sr-only label, replace only the visible price text */
  replacePriceText(element, text) {
    const srOnly = element.querySelector('.sr-only');
    element.textContent = '';

    if (srOnly) {
      element.appendChild(srOnly);
    }

    element.appendChild(document.createTextNode(text));
  }
}

if (!window.customElements.get('bundle-offer')) {
  window.customElements.define('bundle-offer', BundleOffer);
}

// Chaque changement d'offre (clic direct ou sélection d'une pastille de
// variante, qui rejoue un événement change) resynchronise la bascule.
document.addEventListener('change', (event) => {
  if (event.target instanceof Element && event.target.matches('input[data-bundle-offer]')) {
    syncBundleExpressSwap(event.target);
  }
});

/* ------------------------------------------------------------------
   Form submission interception (installed once — this file is a module)
   ------------------------------------------------------------------ */

async function addBundleToCart(form, radio, options = {}) {
  if (!form.checkValidity()) {
    form.reportValidity();
    return;
  }

  const submitButtons = Array.from(form.elements).filter((button) => button.type === 'submit');

  if (options.trigger) {
    submitButtons.push(options.trigger);
  }

  if (submitButtons.some((button) => button.getAttribute('aria-busy') === 'true')) {
    return;
  }

  submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));
  document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

  const sectionsToBundle = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
  );

  const formData = new FormData(form);
  const mainVariantId = parseInt(form.elements.id.value, 10);
  const quantity = parseInt(formData.get('quantity'), 10) || 1;

  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match) {
      properties[match[1]] = value;
    }
  }

  const mainItem = { id: mainVariantId, quantity };
  if (Object.keys(properties).length > 0) {
    mainItem.properties = properties;
  }

  const payload = {
    items: [mainItem, { id: parseInt(radio.dataset.companionId, 10), quantity }],
    sections: sectionsToBundle.join(','),
    sections_url: `${Shopify.routes.root}variants/${mainVariantId}`
  };

  // « Acheter maintenant » : l'état de chargement est conservé jusqu'à la navigation
  let keepLoading = false;

  try {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: JSON.stringify(payload),
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    const responseJson = await response.json().catch(() => ({}));

    if (response.ok && options.checkout) {
      keepLoading = true;
      window.location.href = `${Shopify.routes.root}checkout`;
      return;
    }

    if (response.ok) {
      if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
        keepLoading = true;
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent['sections'] = responseJson['sections'];

      form.dispatchEvent(
        new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
            cart: cartContent,
            onSuccessDo: formData.get('on_success')
          }
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            onSuccessDo: formData.get('on_success'),
            cart: cartContent
          }
        })
      );
    } else {
      form.dispatchEvent(
        new CustomEvent('cart:error', {
          bubbles: true,
          detail: {
            error: responseJson['description']
          }
        })
      );

      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  } catch (error) {
    // Réseau coupé ou réponse illisible : les boutons sont libérés dans finally
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  } finally {
    submitButtons.forEach((button) => button.removeAttribute('aria-busy'));

    if (!keepLoading) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;

    if (!(form.getAttribute('action') || '').includes('/cart/add')) {
      return;
    }

    const radio = document.querySelector(
      `input[data-bundle-offer][data-form-id="${CSS.escape(form.getAttribute('id') || '')}"]:checked`
    );

    if (!radio || !radio.dataset.companionId) {
      return; // no bundle selected — let the theme handle the submission
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    addBundleToCart(form, radio);
  },
  { capture: true }
);

/* ------------------------------------------------------------------
   « Acheter maintenant » pour une offre bundle : ajoute les deux articles
   puis ouvre le paiement (la réduction automatique s'applique au checkout)
   ------------------------------------------------------------------ */

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-bundle-buy-now]');

  if (!button) {
    return;
  }

  const form = document.forms[button.dataset.formId];

  if (!form) {
    return;
  }

  event.preventDefault();

  const radio = document.querySelector(
    `input[data-bundle-offer][data-form-id="${CSS.escape(form.getAttribute('id') || '')}"]:checked`
  );

  if (!radio || !radio.dataset.companionId) {
    form.requestSubmit(); // pas d'article associé : ajout classique au panier
    return;
  }

  addBundleToCart(form, radio, { checkout: true, trigger: button });
});
